import { useEffect, useState } from 'react';

import { Button } from 'components/button';
import { Drawer, DrawerContent, DrawerHeader } from 'components/drawer';
import { Flex } from 'components/flex';
import { SelectMenu } from 'components/select-menu';
import { Switch } from 'components/switch';
import { Text } from 'components/text';
import {
  useNotifications,
  useSendTestNotification,
  useUpdateNotifications,
  useUpdateNotificationsStatus,
} from 'data/notifications';

import { NotificationStatus } from 'interfaces';

import { NotificationList } from './NotificationList';

export type NotificationsProps = {
  isOpen?: boolean;
  onClose?(): void;
};

interface Option {
  label: string;
  value: string;
}

export function Notifications(props: NotificationsProps) {
  const { isOpen, onClose } = props;

  const notifications = useNotifications();
  const updateNotifications = useUpdateNotifications();
  const sendTestNotification = useSendTestNotification();
  const updateNotificationsStatus = useUpdateNotificationsStatus();

  const [filter, setFilter] = useState<string[]>([]);
  const [options, setOptions] = useState<Option[]>([]);

  useEffect(() => {
    const allResourceTypes = notifications.items.map((item) => item.resourceType);
    const validResourceTypes = Array.from(
      new Set(allResourceTypes.filter((rt): rt is string => rt !== null))
    );

    const filterOptions = validResourceTypes.map((resourceType) => ({
      label: resourceType,
      value: resourceType,
    }));

    setOptions(filterOptions);
  }, [notifications.items]);

  const onNavigate = () => {
    onClose?.();
  };

  const toggleShowAll = (showAll: boolean) => {
    updateNotifications(showAll, 0);
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to clear all notifications?')) {
      updateNotificationsStatus(NotificationStatus.Read);
    }
  };

  return (
    <Drawer open={isOpen} onClose={onClose}>
      <DrawerHeader>
        <Flex justify="space-between">
          <Text as="div" variant="title2">
            Notifications
          </Text>

          <Button size="sm" onClick={sendTestNotification}>
            Send test
          </Button>
        </Flex>
      </DrawerHeader>

      <DrawerContent>
        <Flex justify="space-between">
          <Button colorScheme={'primary'} onClick={handleClearAll}>
            Clear all
          </Button>
          <Switch checked={notifications.showAll} onChange={toggleShowAll}>
            Show all
          </Switch>
        </Flex>

        <Flex justify={'end'}>
          <SelectMenu
            label="Filter by Resource Type"
            source={options}
            labelProp={(item) => item.label}
            valueProp={(item) => item.value}
            onClear={() => setFilter([])}
            multiple={true}
            value={filter}
            onChange={(value) => setFilter(value || [])}
          />
        </Flex>

        <NotificationList
          onNavigate={onNavigate}
          resourceTypeFilter={filter} // Pass the filter down
        />
      </DrawerContent>
    </Drawer>
  );
}
